const FILTER_PARAMS = new Set([
  'filter.p.m.custom.subject', 'filter.p.m.custom.styles', 'filter.p.m.custom.rooms',
  'filter.p.m.custom.shape', 'filter.v.m.custom.color_family',
]);
const ROOT_SELECTOR = '[data-v2-shop][data-home-shop-counts-root]';
const pending = new WeakMap();

function decodeFilterValue(value) {
  const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
  // Liquid filter values are HTML-escaped. Decode once so literal entities stay literal.
  return value.replace(/&(amp|lt|gt|quot|apos|#\d+|#x[\da-f]+);/gi, (entity, name) => {
    if (name[0] !== '#') return named[name.toLowerCase()];
    const hexadecimal = name[1].toLowerCase() === 'x';
    const point = Number.parseInt(name.slice(hexadecimal ? 2 : 1), hexadecimal ? 16 : 10);
    return point > 0 && point <= 0x10ffff && !(point >= 0xd800 && point <= 0xdfff)
      ? String.fromCodePoint(point) : entity;
  });
}

export function parseHomeShopCountsPayload(html) {
  try {
    // Read JSON without inserting the returned HTML into the page.
    const scripts = [...html.matchAll(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi)]
      .filter(([, attributes]) => /(?:^|\s)data-home-shop-counts-payload(?:\s|=|$)/i.test(attributes)
        && /(?:^|\s)type\s*=\s*(["'])application\/json\1/i.test(attributes));
    if (scripts.length !== 1) return null;
    const payload = JSON.parse(scripts[0][2]);
    if (payload?.collectionHandle !== 'wall-art' || !Array.isArray(payload.filters)) return null;
    const params = new Set();
    for (const filter of payload.filters) {
      if (!filter || typeof filter.param !== 'string' || !filter.param || params.has(filter.param)
        || !Array.isArray(filter.values)) return null;
      params.add(filter.param);
      const values = new Set();
      for (const item of filter.values) {
        if (!item || typeof item.value !== 'string'
          || !Number.isSafeInteger(item.count) || item.count < 0) return null;
        item.value = decodeFilterValue(item.value);
        if (values.has(item.value)) return null;
        values.add(item.value);
      }
    }
    return payload;
  } catch {
    return null;
  }
}

export function resolveHomeShopCount(payload, href, pageUrl = 'https://hewn.earth/') {
  try {
    const page = new URL(pageUrl);
    const destination = new URL(href, page);
    const filters = [...destination.searchParams];
    if (payload?.collectionHandle !== 'wall-art' || destination.origin !== page.origin
      || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/wall-art\/?$/.test(destination.pathname)
      || filters.length !== 1 || !FILTER_PARAMS.has(filters[0][0]) || !filters[0][1]) return null;
    const [param, value] = filters[0];
    const family = payload.filters.find((filter) => filter.param === param);
    if (!family) return null;
    const match = family.values.find((item) => item.value === value);
    if (!match) return 0;
    return Number.isSafeInteger(match.count) && match.count >= 0 ? match.count : null;
  } catch {
    return null;
  }
}

export function loadHomeShopCounts(section, {
  fetchImpl = globalThis.fetch,
  pageUrl = globalThis.window?.location?.href || 'https://hewn.earth/',
  timeoutMs = 8000,
} = {}) {
  if (!section) return Promise.resolve(false);
  if (pending.has(section)) return pending.get(section);
  const task = (async () => {
    let timer;
    try {
      const rows = [...section.querySelectorAll('[data-v2-shop-row]')]
        .map((row) => ({ row, span: row.querySelector('[data-home-shop-count]') }))
        .filter(({ span }) => span);
      for (const { span } of rows) span.textContent = 'View pieces';
      if (!rows.length) return false;
      const page = new URL(pageUrl);
      const root = (section.getAttribute('data-home-shop-counts-root') || '/').replace(/\/+$/, '');
      const endpoint = new URL(`${root}/collections/wall-art`, page);
      if (endpoint.origin !== page.origin) return false;
      endpoint.searchParams.set('section_id', 'home-shop-counts');
      const preview = page.searchParams.get('preview_theme_id');
      if (preview) endpoint.searchParams.set('preview_theme_id', preview);
      const controller = new AbortController();
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Homepage counts timed out'));
        }, Number.isFinite(timeoutMs) ? Math.min(8000, Math.max(1, timeoutMs)) : 8000);
      });
      const read = (async () => {
        const response = await fetchImpl(endpoint.href, {
          method: 'GET', cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
        });
        if (!response.ok) throw new Error('Homepage counts request failed');
        return response.text();
      })();
      const payload = parseHomeShopCountsPayload(await Promise.race([read, deadline]));
      if (!payload) return false;
      for (const { row, span } of rows) {
        const count = resolveHomeShopCount(payload, row.getAttribute('href'), pageUrl);
        if (count !== null) span.textContent = `${count.toLocaleString('en-US')} ${count === 1 ? 'piece' : 'pieces'}`;
      }
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  })();
  pending.set(section, task);
  task.finally(() => pending.delete(section));
  return task;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const initialize = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) void loadHomeShopCounts(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach((section) => { void loadHomeShopCounts(section); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('pageshow', (event) => { if (event.persisted) initialize(); });
}
